import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { SearchCallCenterContactsFn } from '@/features/freshbooks'
import type { CallCenterContact } from '@/features/freshbooks/CallCenterContact'
import {
  type CallCenterSearchResult,
  type CallCenterWorkspaceMemberMatch,
  dedupeCallCenterMatches,
  resultKeyFromHubSpotContact,
  resultKeyFromMember,
  sortMatchesByLatestLogin,
} from './call-center-search-result'
import { CallCenterSelectionViewModel } from './CallCenterSelectionViewModel'
import { CallCenterJobHistoryViewModel } from './CallCenterJobHistoryViewModel'
import {
  filterCallCenterMemberships,
  filterCallCenterWorkspaceMembersMatches,
} from './call-center-util'
import type {
  ListUserWorkspaceMembershipsFn,
  FetchWorkspaceFn,
  GetCurrentUserWorkspaceMembershipsFn,
  SearchMembersFn,
} from '@/features/iam'
import type { PublishedLegendFn, SearchJobsFn } from '@/features/jobs'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import { Subject } from 'rxjs'
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { withAbortSignal } from '@/utils/rx-util'
import { ViewModelList } from '@/infra/view-model-list'
import { CallCenterSearchResultItemViewModel } from './CallCenterSearchResultItemViewModel'
import { type CallCenterMembershipItemActions } from './CallCenterMembershipItemViewModel'

const SEARCH_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 750

interface ContactResults {
  contacts: CallCenterContact[]
  hasMore: boolean
}

interface MemberResults {
  matches: CallCenterWorkspaceMemberMatch[]
  hasMore: boolean
}

export class CallCenterCustomerLookupViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Search cursors per source. `null` means the source has no more pages,
   * `undefined` that its first page has not been fetched.
   */
  private _contactCursor: string | null | undefined = undefined
  private _workspaceIds: Id[] = []
  private readonly _workspaceCursors = new Map<Id, string | null | undefined>()
  private _aggregateCursorIndex = 0

  /**
   * Results by email (or contact/member ID), so a HubSpot contact and the
   * workspace members with the same email show as one result.
   */
  private readonly _resultLookup = new Map<string, CallCenterSearchResult>()

  private readonly _selections = new Subject<CallCenterSearchResult>()

  private readonly _resultVms = new ViewModelList({
    models: () => this.scroller.items.slice(),
    create: (result: CallCenterSearchResult) =>
      new CallCenterSearchResultItemViewModel(
        result,
        (r) => this.callCenterSelectionVm.selectedResult?.key === r.key,
        (r) => this.selectResult(r),
      ),
  })

  readonly callCenterSelectionVm: CallCenterSelectionViewModel

  readonly callCenterJobHistoryVm: CallCenterJobHistoryViewModel

  readonly searchText = new SearchTextViewModel(SEARCH_DEBOUNCE_MS)

  readonly scroller = new InfiniteScrollProvider<
    CallCenterSearchResult,
    string
  >({
    limit: SEARCH_LIMIT,
    fetchItems: ({ paging, signal, filter }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Searching customers failed',
        signal,
      )
      return withRetry(() =>
        this._fetchSearchResults(filter ?? '', paging, signal),
      )
    },
  })

  @observable
  accessor lastQuery = ''

  constructor(
    private readonly _searchCallCenterContacts: SearchCallCenterContactsFn,
    private readonly _searchMembers: SearchMembersFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    listUserWorkspaceMemberships: ListUserWorkspaceMembershipsFn,
    fetchWorkspace: FetchWorkspaceFn,
    searchJobs: SearchJobsFn,
    publishedLegend: PublishedLegendFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    membershipItemActions: CallCenterMembershipItemActions,
  ) {
    this.callCenterSelectionVm = new CallCenterSelectionViewModel(
      listUserWorkspaceMemberships,
      fetchWorkspace,
      membershipItemActions,
    )

    this.callCenterJobHistoryVm = new CallCenterJobHistoryViewModel(
      searchJobs,
      publishedLegend,
      _createAlertRetryWrapper,
    )

    this._lifecycle
      .manage(this.callCenterSelectionVm)
      .manage(this.callCenterJobHistoryVm)
      .manage(this._resultVms)
      .manage(
        this.searchText.debouncedValue$.pipe(
          map((value) => value.trim()),
          distinctUntilChanged(),
          switchMap((value) => this._performSearch(value)),
        ),
      )
      .manage(
        this._selections.pipe(
          switchMap((result) =>
            withAbortSignal((signal) => this._select(result, signal)),
          ),
        ),
      )
  }

  @computed
  get results() {
    return this.scroller.items
  }

  /**
   * The search results.
   */
  @computed
  get resultVms() {
    return this._resultVms.items
  }

  @computed
  get isSearching() {
    return (
      this.lastQuery.length > 0 &&
      this.results.length === 0 &&
      this.scroller.fetch.pending
    )
  }

  @computed
  get shouldShowResultsLoader() {
    if (!this.lastQuery) {
      return false
    }

    if (this.scroller.fetch.pending && this.results.length === 0) {
      return false
    }

    return this.results.length > 0 && !this.scroller.isDone
  }

  @computed
  get shouldShowEmptyState() {
    return !this.isSearching && this.results.length === 0
  }

  @computed
  get showAddClientButton() {
    return this.shouldShowEmptyState && this.lastQuery.length > 0
  }

  /**
   * Searches right away instead of waiting for the debounce.
   */
  @action.bound
  submitSearch() {
    this.searchText.change(this.searchText.value, true)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the result's details, cancelling the loading of the details of the
   * result selected before.
   */
  @action.bound
  selectResult(result: CallCenterSearchResult) {
    this._selections.next(result)
  }

  private async _select(result: CallCenterSearchResult, signal: AbortSignal) {
    // Get initial workspace matches.
    let workspaceMatches = filterCallCenterWorkspaceMembersMatches(
      result.workspaceMembers,
    )

    // If no workspace members, try to find them by email.
    if (workspaceMatches.length === 0) {
      workspaceMatches = await this._ensureWorkspaceMembersForResult(
        result,
        signal,
      )
      if (signal.aborted) {
        return
      }
    }

    // Set initial selection state.
    const initialMemberships = workspaceMatches.map((match) => ({
      workspace: match.workspace,
      member: match.member,
    }))

    this.callCenterSelectionVm.setSelectedResult(result, initialMemberships)

    const userPublicIds = [
      ...new Set(
        workspaceMatches
          .map((match) => match.member.user.publicId)
          .filter(Id.some),
      ),
    ]

    if (userPublicIds.length === 0) {
      this.callCenterJobHistoryVm.clear()
      return
    }

    this.callCenterJobHistoryVm.fetchJobs(userPublicIds)

    const withRetry = this._createAlertRetryWrapper(
      "Loading the client's workspaces failed",
      signal,
    )
    // The workspaces found by the search stay when the alert is dismissed.
    await withRetry(() =>
      this.callCenterSelectionVm.fetchMembershipDetails(userPublicIds, signal),
    ).catch(() => undefined)
  }

  private _prepareWorkspaceContext() {
    this._workspaceIds = this._callCenterWorkspaceIds()
    this._workspaceIds.forEach((workspaceId) => {
      this._workspaceCursors.set(workspaceId, undefined)
    })
  }

  private _resetSearch() {
    this._contactCursor = undefined
    this._workspaceIds = []
    this._workspaceCursors.clear()
    this._aggregateCursorIndex = 0
    this._resultLookup.clear()
  }

  private _callCenterWorkspaceIds() {
    return filterCallCenterMemberships(
      this._getCurrentUserWorkspaceMemberships(),
    )
      .map((membership) => membership.workspace.id)
      .filter(Id.some)
  }

  private async _fetchSearchResults(
    query: string,
    paging: CursorPageQuery,
    signal: AbortSignal,
  ): Promise<CursorPaged<CallCenterSearchResult>> {
    if (!query || query !== this.lastQuery) {
      return CursorPaged.empty()
    }

    const [contactsPage, workspacePage] = await Promise.all([
      this._fetchContactResults(query, paging.limit, signal),
      this._fetchWorkspaceMatches(query, paging.limit, signal),
    ])

    signal.throwIfAborted()

    const newResults: CallCenterSearchResult[] = []
    runInAction(() => {
      newResults.push(...this._mergeContacts(contactsPage.contacts))
      newResults.push(...this._mergeWorkspaceMembers(workspacePage.matches))
    })

    const hasMore = contactsPage.hasMore || workspacePage.hasMore

    return {
      data: newResults,
      next: hasMore ? this._nextAggregateCursor() : null,
    }
  }

  private async _fetchContactResults(
    search: string,
    limit: number,
    signal: AbortSignal,
  ): Promise<ContactResults> {
    if (this._contactCursor === null) {
      return { contacts: [], hasMore: false }
    }

    const response = await this._searchCallCenterContacts(
      { search, limit, after: this._contactCursor ?? undefined },
      signal,
    )

    signal.throwIfAborted()

    this._contactCursor = response.next

    return {
      contacts: response.data,
      hasMore: this._contactCursor !== null,
    }
  }

  private async _fetchWorkspaceMatches(
    search: string,
    limit: number,
    signal: AbortSignal,
  ): Promise<MemberResults> {
    const workspaceIdsToFetch = this._workspaceIds.filter(
      (workspaceId) => this._workspaceCursors.get(workspaceId) !== null,
    )

    if (workspaceIdsToFetch.length === 0) {
      return { matches: [], hasMore: false }
    }

    const responses = await Promise.all(
      workspaceIdsToFetch.map(async (workspaceId) => {
        const after = this._workspaceCursors.get(workspaceId) ?? undefined
        try {
          const response = await this._searchWorkspaceMembers(
            workspaceId,
            search,
            limit,
            after,
            signal,
          )
          return { workspaceId, response }
        } catch {
          return { workspaceId, response: null }
        }
      }),
    )

    signal.throwIfAborted()

    const matches: CallCenterWorkspaceMemberMatch[] = []

    responses.forEach(({ workspaceId, response }) => {
      if (!response) {
        this._workspaceCursors.set(workspaceId, null)
        return
      }

      response.data.forEach((member) => {
        matches.push({ member, workspace: member.workspace })
      })

      this._workspaceCursors.set(workspaceId, response.next || null)
    })

    return {
      matches: sortMatchesByLatestLogin(
        filterCallCenterWorkspaceMembersMatches(
          dedupeCallCenterMatches(matches),
        ),
      ),
      hasMore: this._workspaceIds.some(
        (workspaceId) => this._workspaceCursors.get(workspaceId) !== null,
      ),
    }
  }

  private _searchWorkspaceMembers(
    workspaceId: Id,
    searchText: string,
    limit: number,
    after: string | undefined,
    signal: AbortSignal,
  ) {
    return this._searchMembers(
      {
        searchText,
        limit,
        workspaceId: Id.assert(workspaceId),
        userTypes: ['User'],
        after,
      },
      signal,
    )
  }

  private _mergeContacts(hubspotContacts: CallCenterContact[]) {
    const newlyAdded: CallCenterSearchResult[] = []

    hubspotContacts.forEach((hubspotContact) => {
      const key = resultKeyFromHubSpotContact(hubspotContact)
      const existing = this._resultLookup.get(key)

      if (existing) {
        existing.hubspotContact = hubspotContact
        return
      }

      const result = observable<CallCenterSearchResult>({
        key,
        hubspotContact,
        workspaceMembers: [],
      })

      this._resultLookup.set(key, result)
      newlyAdded.push(result)
    })

    return newlyAdded
  }

  private _mergeWorkspaceMembers(
    workspaceMembersMatches: CallCenterWorkspaceMemberMatch[],
  ) {
    const newlyAdded: CallCenterSearchResult[] = []

    workspaceMembersMatches.forEach((match) => {
      const key = resultKeyFromMember(match.member)
      const existing = this._resultLookup.get(key)

      if (existing) {
        existing.workspaceMembers = sortMatchesByLatestLogin(
          dedupeCallCenterMatches(existing.workspaceMembers.concat(match)),
        )
        return
      }

      const result = observable<CallCenterSearchResult>({
        key,
        hubspotContact: null,
        workspaceMembers: [match],
      })

      this._resultLookup.set(key, result)
      newlyAdded.push(result)
    })

    return newlyAdded
  }

  private _nextAggregateCursor(): string {
    this._aggregateCursorIndex += 1
    return `${this._aggregateCursorIndex}`
  }

  private async _ensureWorkspaceMembersForResult(
    result: CallCenterSearchResult,
    signal: AbortSignal,
  ): Promise<CallCenterWorkspaceMemberMatch[]> {
    const contact = result.hubspotContact
    if (!contact?.email) {
      return result.workspaceMembers
    }

    const matches = await this._searchWorkspaceMembersByEmail(
      contact.email,
      signal,
    )

    if (matches.length > 0) {
      runInAction(() => {
        result.workspaceMembers = matches
        const existingResult = this._resultLookup.get(result.key)
        if (existingResult) {
          existingResult.workspaceMembers = matches
        }
      })
    }

    return matches
  }

  private async _searchWorkspaceMembersByEmail(
    email: string,
    signal: AbortSignal,
  ) {
    const workspaceIds = this._callCenterWorkspaceIds()
    if (workspaceIds.length === 0) {
      return []
    }

    const responses = await Promise.all(
      workspaceIds.map((workspaceId) =>
        this._searchWorkspaceMembers(
          workspaceId,
          email,
          SEARCH_LIMIT,
          undefined,
          signal,
        ).catch(() => null),
      ),
    )

    const matches: CallCenterWorkspaceMemberMatch[] = []
    responses.forEach((response) => {
      response?.data.forEach((member) => {
        matches.push({ member, workspace: member.workspace })
      })
    })

    return sortMatchesByLatestLogin(
      filterCallCenterWorkspaceMembersMatches(dedupeCallCenterMatches(matches)),
    )
  }

  private async _performSearch(query: string) {
    this._setLastQuery(query)

    if (!query) {
      this._clearResults()
      return
    }

    this._clearResults()
    this._prepareWorkspaceContext()

    try {
      await this.scroller.fetch({
        filter: query,
        signal: this._lifecycle.signal,
      })
    } catch {
      // The retry alert has reported the failure.
      if (query === this.lastQuery) {
        this._clearResults()
      }
    }
  }

  @action.bound
  private _setLastQuery(value: string) {
    this.lastQuery = value
  }

  @action.bound
  private _clearResults() {
    this.scroller.reset()
    this._resetSearch()
    this.callCenterSelectionVm.clear()
    this.callCenterJobHistoryVm.clear()
  }
}
